/**
 * TreeCheckboxGroup — Collapsible two-level provider → model checkbox tree.
 *
 * Providers are shown as clickable accordion rows. Clicking expands to reveal
 * model checkboxes. Providers with at least one selected model start expanded
 * and show a selection count badge when collapsed.
 *
 * Props:
 *   providers     [{ id, name, models: [{ id, name }] }]
 *   selectedIds   number[]
 *   onToggle      fn(id)
 *   hasOther      bool
 *   otherValue    string
 *   onOtherChange fn(str)
 *   error         string
 */
import { useState } from 'react'

export default function TreeCheckboxGroup({
  providers,
  selectedIds,
  onToggle,
  hasOther = false,
  otherValue = '',
  onOtherChange,
  error,
}) {
  // Providers with pre-existing selections start open
  const initialOpen = new Set(
    providers
      .filter((p) => p.models.some((m) => selectedIds.includes(m.id)))
      .map((p) => p.id)
  )
  const [openIds, setOpenIds] = useState(initialOpen)

  const toggleProvider = (providerId) =>
    setOpenIds((prev) => {
      const next = new Set(prev)
      next.has(providerId) ? next.delete(providerId) : next.add(providerId)
      return next
    })

  return (
    <div className="tree-checkbox-root">
      {providers.map((provider) => {
        const isOpen = openIds.has(provider.id)
        const selectedCount = provider.models.filter((m) =>
          selectedIds.includes(m.id)
        ).length

        return (
          <div
            key={provider.id}
            className={`tree-provider${isOpen ? ' tree-provider--open' : ''}`}
          >
            <button
              type="button"
              className="tree-provider-header"
              onClick={() => toggleProvider(provider.id)}
              aria-expanded={isOpen}
            >
              <span className="tree-provider-chevron" aria-hidden="true">
                {isOpen ? '\u25be' : '\u25b8'}
              </span>
              <span className="tree-provider-name">{provider.name}</span>
              {!isOpen && selectedCount > 0 && (
                <span className="tree-provider-badge">{selectedCount} selected</span>
              )}
            </button>

            {isOpen && (
              <div className="tree-provider-models">
                <div className="checkbox-group">
                  {provider.models.map((model) => (
                    <label key={model.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(model.id)}
                        onChange={() => onToggle(model.id)}
                      />
                      {model.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      })}

      {hasOther && (
        <div className="checkbox-other" style={{ marginTop: '0.75rem' }}>
          <label>Other:</label>
          <input
            type="text"
            className="form-control"
            value={otherValue}
            onChange={(e) => onOtherChange(e.target.value)}
            placeholder="Specify other model: provider/model name version"
            aria-label="Other AI model"
          />
        </div>
      )}

      {error && <span className="field-error">{error}</span>}
    </div>
  )
}
